import { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { PageProps, PendingEquityWarningPayload } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import DataTable, { Column } from '@/components/DataTable'
import { Award, DollarSign, TrendingUp } from 'lucide-react'

interface EquityCredit {
  id: number
  credit_amount: number
  credit_type: string
  event_name: string
  event_date: string
  notes: string | null
  source_type: string | null
  source_id: number | null
  granted_by_name: string | null
  created_at: string
}

interface Totals {
  total: number
  production: number
  override: number
  bonus: number
  credit_count: number
}

interface Props extends PageProps {
  credits: EquityCredit[]
  totals: Totals
  pending_equity_warning?: PendingEquityWarningPayload | null
}

export default function MyEquityIndex({ credits, totals, pending_equity_warning }: Props) {
  const [activeTab, setActiveTab] = useState<'all' | 'production' | 'override'>('all')

  // `pending_count` is omitted entirely when the organization hides commission
  // amounts, so treat a missing count as zero and keep the note hidden.
  const pendingCreditCount = pending_equity_warning?.pending_count ?? 0

  const typeColors: Record<string, string> = {
    production: 'bg-green-100 text-green-700',
    override: 'bg-blue-100 text-blue-700',
    bonus: 'bg-purple-100 text-purple-700',
    adjustment: 'bg-orange-100 text-orange-700'
  }

  const typeLabels: Record<string, string> = {
    production: 'Direct Sale',
    override: 'Sponsor Override',
    bonus: 'Bonus',
    adjustment: 'Adjustment'
  }

  const filteredCredits = activeTab === 'all'
    ? credits
    : credits.filter(c => c.credit_type === activeTab)

  const columns: Column<EquityCredit>[] = [
    {
      key: 'event_name',
      header: 'Description',
      sortable: true,
      render: (credit) => (
        <div>
          <div className="flex items-center gap-2">
            <Award className="h-4 w-4 text-chestnut-gold flex-shrink-0" />
            <span className="text-sm text-chestnut-charcoal font-medium">{credit.event_name}</span>
          </div>
          {credit.notes && (
            <p className="text-xs text-chestnut-charcoal/60 mt-1 ml-6">{credit.notes}</p>
          )}
        </div>
      )
    },
    {
      key: 'credit_type',
      header: 'Source',
      sortable: true,
      render: (credit) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${typeColors[credit.credit_type]}`}>
          {typeLabels[credit.credit_type] || credit.credit_type}
        </span>
      )
    },
    {
      key: 'credit_amount',
      header: 'Credits',
      sortable: true,
      className: 'text-right',
      headerClassName: 'text-right',
      render: (credit) => (
        <span className="font-semibold text-chestnut-forest">+{credit.credit_amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
      )
    },
    {
      key: 'event_date',
      header: 'Date',
      sortable: true,
      render: (credit) => credit.event_date ? new Date(credit.event_date).toLocaleDateString() : '-'
    },
    {
      key: 'granted_by_name',
      header: 'Source',
      sortable: true,
      className: 'text-chestnut-charcoal/60',
      render: (credit) => credit.source_type === 'Commission' ? 'Auto (Commission)' : (credit.granted_by_name || 'Manual')
    }
  ]

  return (
    <AppLayout>
      <Head title="My Equity Credits" />

      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">My Equity Credits</h1>
          <p className="text-chestnut-charcoal/70 mt-1">You earn 1 equity credit for every $1 in commission received.</p>
        </div>

        {pending_equity_warning && pendingCreditCount > 0 && pending_equity_warning.earliest_expires_at && (
          <p className="text-sm text-chestnut-charcoal/80 px-1">
            Heads up: the {pendingCreditCount} pending credit
            {pendingCreditCount === 1 ? '' : 's'} above
            {pending_equity_warning.days_remaining !== null
              ? ` expire on ${new Date(pending_equity_warning.earliest_expires_at).toLocaleDateString()}`
              : ' have an expiration date'}{' '}
            unless you sign your agent agreement.{' '}
            {pending_equity_warning.signing_url ? (
              <a
                href={pending_equity_warning.signing_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-chestnut-forest font-medium underline hover:no-underline"
              >
                View &amp; sign the agreement
              </a>
            ) : (
              <a
                href={pending_equity_warning.resend_url}
                onClick={(e) => {
                  e.preventDefault()
                  router.post(pending_equity_warning.resend_url, {}, { preserveScroll: true })
                }}
                className="text-chestnut-forest font-medium underline hover:no-underline"
              >
                Email me the agreement
              </a>
            )}
            .
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-chestnut-gray p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-chestnut-gold/10 rounded-lg">
                <Award className="h-5 w-5 text-chestnut-gold" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{totals.total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
                <p className="text-xs text-chestnut-charcoal/60">Total Credits</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-chestnut-gray p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-green-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{totals.production.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
                <p className="text-xs text-chestnut-charcoal/60">From Direct Sales</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-chestnut-gray p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-blue-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{totals.override.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
                <p className="text-xs text-chestnut-charcoal/60">From Sponsor Overrides</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-chestnut-gray">
          <div className="border-b border-chestnut-gray">
            <div className="flex">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'all'
                    ? 'border-chestnut-forest text-chestnut-forest'
                    : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal'
                }`}
              >
                All Credits ({credits.length})
              </button>
              <button
                onClick={() => setActiveTab('production')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'production'
                    ? 'border-chestnut-forest text-chestnut-forest'
                    : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal'
                }`}
              >
                Direct Sales ({credits.filter(c => c.credit_type === 'production').length})
              </button>
              <button
                onClick={() => setActiveTab('override')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'override'
                    ? 'border-chestnut-forest text-chestnut-forest'
                    : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal'
                }`}
              >
                Sponsor Overrides ({credits.filter(c => c.credit_type === 'override').length})
              </button>
            </div>
          </div>

          <div className="p-6">
            <DataTable
              data={filteredCredits}
              columns={columns}
              searchPlaceholder="Search equity credits..."
              searchKeys={['event_name']}
              pageSize={15}
              emptyMessage="No equity credits yet. Credits are earned automatically from commissions."
              getRowKey={(c) => c.id}
            />
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
